class RedirectBar extends HTMLElement {
  constructor() {
    super();
    this.ticking = false;
    this.targets = [];
    this.placeholder = null;
    this.clickLock = false;
    this.clickLockTimeout = null;
  }

  connectedCallback() {
    this.links = this.querySelectorAll('.redirect-bar__link');
    this.sectionWrapper = this.closest('.shopify-section');
    this.barHeight = this.offsetHeight;
    this.barOffsetTop = this.sectionWrapper ? this.sectionWrapper.offsetTop : this.offsetTop;
    this.pageHeader = document.querySelector('.section-header-v2, .section-header');
    this.createPlaceholder();
    this.collectTargets();
    this.markActivePageLinks();
    this.bindScrollHandler();
    this.bindResizeHandler();
    this.bindClickHandlers();
    this.onScroll();
    this.restoreBarViewportPosition();

    // The reading above is taken before images and fonts above the bar have
    // settled, and the bar's own height changes at the 767px breakpoint. A stale
    // threshold sticks the bar at the wrong scroll position, so take it again
    // once the page has loaded.
    window.addEventListener('load', () => this.measure(), { once: true });
  }

  // Everything the sticky threshold depends on, re-read together.
  measure = () => {
    this.barHeight = this.offsetHeight;
    this.placeholder.style.height = this.barHeight + 'px';
    this.barOffsetTop = this.getBarDocumentTop();
    this.onScroll();
  };

  // Key used to carry the bar's on-screen position across a page navigation.
  static RETURN_KEY = 'redirectBarViewportTop';

  // Document-relative top of the bar's natural (non-sticky) position.
  getBarDocumentTop = () => {
    return this.sectionWrapper ? this.sectionWrapper.offsetTop : this.offsetTop;
  };

  // Remember where the bar currently sits in the viewport so the destination
  // page (which shares the same hero/section above the bar) can land at the
  // same spot instead of jumping to the very top.
  storeBarViewportPosition = () => {
    try {
      if (this.classList.contains('is-sticky')) {
        // Sticky at navigation time: the destination page should pin the bar to
        // the top with the section directly below it (hero scrolled away).
        sessionStorage.setItem(RedirectBar.RETURN_KEY, 'sticky');
      } else {
        const viewportTop = this.getBarDocumentTop() - window.scrollY;
        sessionStorage.setItem(RedirectBar.RETURN_KEY, String(viewportTop));
      }
    } catch (e) {
      /* sessionStorage unavailable — fall back to default top navigation */
    }
  };

  restoreBarViewportPosition = () => {
    let stored = null;
    try {
      stored = sessionStorage.getItem(RedirectBar.RETURN_KEY);
      if (stored !== null) sessionStorage.removeItem(RedirectBar.RETURN_KEY);
    } catch (e) {
      return;
    }
    if (stored === null) return;

    const isStickyReturn = stored === 'sticky';
    const viewportTop = isStickyReturn ? 0 : parseFloat(stored);
    if (!isStickyReturn && Number.isNaN(viewportTop)) return;

    const align = () => {
      const barDocTop = this.getBarDocumentTop();
      // viewport and the next section starts right beneath it.
      // Non-sticky: keep the bar at the same viewport position as before.
      const targetY = isStickyReturn ? barDocTop : Math.max(0, barDocTop - viewportTop);
      window.scrollTo(0, targetY);
      this.onScroll();
    };

    // Align immediately, then again once images/fonts above have settled so a
    // late layout shift doesn't leave the bar off-screen.
    align();
    window.addEventListener('load', align, { once: true });
  };

  createPlaceholder() {
    this.placeholder = document.createElement('div');
    this.placeholder.style.display = 'none';
    this.placeholder.style.height = this.barHeight + 'px';
    this.parentNode.insertBefore(this.placeholder, this.nextSibling);
  }

  collectTargets() {
    this.links.forEach((link) => {
      const href = link.getAttribute('href');
      if (href && href.includes('#')) {
        const hash = href.substring(href.indexOf('#'));
        const el = document.getElementById(hash.substring(1));
        if (el) this.targets.push({ link, section: el });
      }
    });
  }

  normalizePath(path) {
    if (!path) return '';
    return path.replace(/\/+$/, '') || '/';
  }

  markActivePageLinks() {
    const currentPath = this.normalizePath(window.location.pathname);
    this.links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href || href.includes('#')) return;
      if (this.normalizePath(link.pathname) === currentPath) {
        link.classList.add('active');
      }
    });
  }

  bindScrollHandler() {
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        requestAnimationFrame(() => this.onScroll());
        this.ticking = true;
      }
    });
  }

  bindResizeHandler() {
    let pending = false;
    window.addEventListener('resize', () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        this.measure();
      });
    });
  }

  onScroll() {
    const stickyThreshold = this.barOffsetTop - 16;
    const isSticky = this.clickLock || window.scrollY >= stickyThreshold;
    const lastSectionBottom = this.getLastSectionBottom();
    const isPastLastSection = lastSectionBottom !== null && window.scrollY + this.barHeight > lastSectionBottom;

    this.classList.toggle('is-sticky', isSticky);
    this.classList.toggle('is-hidden', isPastLastSection);
    this.placeholder.style.display = isSticky ? 'block' : 'none';

    if (this.pageHeader) {
      this.pageHeader.classList.toggle('header-hidden', isSticky && !isPastLastSection);
    }

    let activeLink = null;
    for (let i = this.targets.length - 1; i >= 0; i--) {
      if (this.targets[i].section.getBoundingClientRect().top < window.innerHeight / 2) {
        activeLink = this.targets[i].link;
        break;
      }
    }
    this.targets.forEach((t) => t.link.classList.remove('active'));
    if (activeLink) {
      activeLink.classList.add('active');
      this.scrollActiveLinkIntoView(activeLink);
    }

    this.ticking = false;
  }

  getLastSectionBottom() {
    if (!this.targets.length) return null;
    let maxBottom = 0;
    for (const t of this.targets) {
      const rect = t.section.getBoundingClientRect();
      const bottom = rect.bottom + window.scrollY;
      if (bottom > maxBottom) maxBottom = bottom;
    }
    return maxBottom;
  }

  scrollActiveLinkIntoView(activeLink) {
    if (this.scrollWidth <= this.clientWidth) return;

    const linkRect = activeLink.getBoundingClientRect();
    const scrollerRect = this.getBoundingClientRect();
    const linkLeftInScroller = linkRect.left - scrollerRect.left + this.scrollLeft;
    const linkRightInScroller = linkLeftInScroller + linkRect.width;
    const visibleLeft = this.scrollLeft;
    const visibleRight = visibleLeft + this.clientWidth;

    if (linkLeftInScroller < visibleLeft || linkRightInScroller > visibleRight) {
      this.scrollTo({
        left: linkLeftInScroller - this.clientWidth / 2 + linkRect.width / 2,
        behavior: 'smooth',
      });
    }
  }

  bindClickHandlers() {
    this.links.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.includes('#')) {
          const hash = href.substring(href.indexOf('#'));
          const target = document.getElementById(hash.substring(1));
          if (target) {
            e.preventDefault();
            this.barOffsetTop = this.sectionWrapper ? this.sectionWrapper.offsetTop : this.offsetTop;
            this.clickLock = true;
            clearTimeout(this.clickLockTimeout);
            this.clickLockTimeout = setTimeout(() => {
              this.clickLock = false;
              this.onScroll();
            }, 800);
            const headerOffset =
              this.pageHeader && !this.pageHeader.classList.contains('header-hidden')
                ? this.pageHeader.offsetHeight
                : 0;
            window.scrollTo({
              top: target.getBoundingClientRect().top + window.scrollY - this.barHeight - 16 - headerOffset,
              behavior: 'smooth',
            });
          }
        } else if (href) {
          // Cross-page link: navigation proceeds normally, but remember the bar's
          // viewport position so the next page restores it (seamless transition).
          this.storeBarViewportPosition();
        }
      });
    });
  }
}

customElements.define('redirect-bar', RedirectBar);
